import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import PageShell, { SectionTitle } from './PageShell';
import { PRE_REQUIS_COMMUNS, ETAPES_COMMUNES, PARCOURS } from '../data/formationsEleve';
import { Calendar, Shield, BadgeCheck, FileText, Eye, Hash, ChevronDown, ArrowLeft, CheckCircle2, AlertTriangle, Sparkles, Users, Clock, Route as RouteIcon, GraduationCap, MapPin, Download, Mail, Loader2 } from 'lucide-react';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const ICON_MAP = { calendar: Calendar, shield: Shield, id: BadgeCheck, file: FileText, eye: Eye, hash: Hash };

function ParcoursCard({ p }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="rounded-2xl border border-white/10 bg-[#0d0d0e] overflow-hidden transition-colors hover:border-red-700/40" data-testid={`parcours-${p.slug}`}>
      {/* Header */}
      <button onClick={() => setOpen((v) => !v)} className="w-full p-6 lg:p-7 text-left">
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1 min-w-0">
            <div className="text-[10.5px] uppercase tracking-[0.22em] text-red-400 font-bold font-mono-spec mb-2">{p.code} · {p.sub}</div>
            <h3 className="font-serif-editorial text-white text-[24px] lg:text-[28px] leading-tight">{p.title}</h3>
            <p className="text-white/65 text-[14px] mt-3 leading-relaxed">{p.intro}</p>
            <div className="mt-4 flex flex-wrap gap-2 text-[11.5px] font-mono-spec">
              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border border-white/10 bg-white/5 text-white/70">
                <Calendar size={11} /> {p.age}
              </span>
              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border border-white/10 bg-white/5 text-white/70">
                <Clock size={11} /> {p.duree_globale}
              </span>
              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border border-red-700/30 bg-red-600/10 text-red-300">
                <RouteIcon size={11} /> {p.heures_min}
              </span>
            </div>
          </div>
          <ChevronDown size={20} className={`text-white/50 shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} />
        </div>
      </button>

      {open && (
        <div className="px-6 lg:px-7 pb-7 border-t border-white/5 space-y-6 pt-5">
          {/* Avantages */}
          <div>
            <div className="text-[10.5px] uppercase tracking-[0.18em] text-emerald-400 font-bold font-mono-spec mb-2 flex items-center gap-2">
              <CheckCircle2 size={12} /> Avantages
            </div>
            <ul className="space-y-1.5">
              {p.avantages.map((a, i) => (
                <li key={i} className="flex items-start gap-2 text-white/75 text-[13.5px] leading-relaxed">
                  <span className="text-emerald-400 shrink-0 mt-1.5 w-1 h-1 rounded-full bg-emerald-400 block" />
                  <span>{a}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Points d'attention */}
          {p.points_attention?.length > 0 && (
            <div>
              <div className="text-[10.5px] uppercase tracking-[0.18em] text-amber-400 font-bold font-mono-spec mb-2 flex items-center gap-2">
                <AlertTriangle size={12} /> Points d'attention
              </div>
              <ul className="space-y-1.5">
                {p.points_attention.map((pt, i) => (
                  <li key={i} className="text-white/75 text-[13.5px] leading-relaxed">{pt}</li>
                ))}
              </ul>
            </div>
          )}

          {/* RDV obligatoires (AAC / CS) */}
          {p.rdv_obligatoires?.length > 0 && (
            <div className="rounded-xl border border-red-700/30 bg-red-950/15 p-5">
              <div className="text-[10.5px] uppercase tracking-[0.22em] text-red-300 font-bold font-mono-spec mb-3 flex items-center gap-2">
                <Sparkles size={12} /> RDV pédagogiques obligatoires
              </div>
              <div className="space-y-5">
                {p.rdv_obligatoires.map((r) => (
                  <div key={r.n} className="rounded-lg bg-[#0a0a0b]/60 border border-white/5 p-4" data-testid={`rdv-${p.slug}-${r.n}`}>
                    <div className="flex items-start justify-between gap-3 mb-2">
                      <div>
                        <div className="text-[10.5px] uppercase tracking-[0.18em] text-red-400 font-bold font-mono-spec">RDV {r.n}</div>
                        <div className="text-white font-serif-editorial text-[18px] leading-tight mt-0.5">{r.titre}</div>
                        <div className="text-red-400/80 text-[12px] font-mono-spec mt-1">{r.label}</div>
                      </div>
                      <span className="px-2.5 py-1 rounded-full bg-red-600 text-white text-[10.5px] font-bold tracking-wider whitespace-nowrap">{r.duree}</span>
                    </div>
                    <p className="text-white/65 text-[13px] italic mb-2.5">{r.objectif}</p>
                    <ul className="space-y-1.5">
                      {r.contenu.map((c, i) => (
                        <li key={i} className="flex items-start gap-2 text-white/80 text-[13px] leading-relaxed">
                          <CheckCircle2 size={12} className="text-red-400 shrink-0 mt-1" />
                          <span>{c}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Rôle accompagnateur (AAC uniquement) */}
          {p.role_accompagnateur && (
            <div className="rounded-xl border border-white/10 bg-[#0a0a0b]/60 p-5">
              <div className="text-[10.5px] uppercase tracking-[0.22em] text-white/55 font-bold font-mono-spec mb-3 flex items-center gap-2">
                <Users size={12} /> {p.role_accompagnateur.titre}
              </div>
              <div className="grid md:grid-cols-3 gap-4 text-[12.5px]">
                <div>
                  <div className="text-emerald-300 font-bold mb-1.5 text-[11px] uppercase tracking-wider">Pré-requis</div>
                  <ul className="space-y-1 text-white/70 leading-relaxed">
                    {p.role_accompagnateur.pre_requis.map((x, i) => <li key={i}>· {x}</li>)}
                  </ul>
                </div>
                <div>
                  <div className="text-sky-300 font-bold mb-1.5 text-[11px] uppercase tracking-wider">Missions</div>
                  <ul className="space-y-1 text-white/70 leading-relaxed">
                    {p.role_accompagnateur.missions.map((x, i) => <li key={i}>· {x}</li>)}
                  </ul>
                </div>
                <div>
                  <div className="text-amber-300 font-bold mb-1.5 text-[11px] uppercase tracking-wider">À éviter</div>
                  <ul className="space-y-1 text-white/70 leading-relaxed">
                    {p.role_accompagnateur.a_eviter.map((x, i) => <li key={i}>· {x}</li>)}
                  </ul>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function ChecklistLeadForm() {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [err, setErr] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    setErr('');
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setErr('Email invalide.');
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/leads/checklist`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, name, source: 'checklist_parcours' }),
      });
      if (!res.ok) throw new Error('Échec du téléchargement');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'checklist-permis-codevoiture.pdf';
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      setDone(true);
    } catch (e2) {
      setErr('Une erreur est survenue. Réessaie dans un instant.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="my-12 rounded-xl sm:rounded-2xl border border-red-700/30 bg-gradient-to-br from-[#15090a] via-[#0d0d0e] to-[#0d0d0e] p-5 sm:p-7 lg:p-9 relative overflow-hidden" data-testid="checklist-lead-form">
      <div className="pointer-events-none absolute -top-20 -right-20 w-72 h-72 rounded-full bg-red-600/10 blur-3xl" />
      <div className="relative z-10 grid lg:grid-cols-2 gap-7 items-center">
        <div>
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-red-700/40 bg-red-600/10 mb-4">
            <Download size={13} className="text-red-400" />
            <span className="text-[10.5px] uppercase tracking-[0.22em] text-red-400 font-bold">Check-list PDF · gratuite</span>
          </div>
          <h3 className="font-serif-editorial text-white text-[26px] lg:text-[34px] leading-[1.05] tracking-tight">
            Reçois la check-list <span className="text-red-500">complète</span> en PDF
          </h3>
          <p className="text-white/65 text-[14px] mt-3 leading-relaxed">
            Tous les documents à préparer pour t'inscrire (Permis B classique, AAC, CS) — section par section, mise à jour selon la réglementation 2026. Imprime-la et coche au fur et à mesure.
          </p>
          <ul className="mt-4 space-y-1.5 text-white/70 text-[13px]">
            <li className="flex items-center gap-2"><CheckCircle2 size={13} className="text-emerald-400" /> Identité, administratif, NEPH, ASSR 2/ASR</li>
            <li className="flex items-center gap-2"><CheckCircle2 size={13} className="text-emerald-400" /> Spécifique AAC + accompagnateur</li>
            <li className="flex items-center gap-2"><CheckCircle2 size={13} className="text-emerald-400" /> Le jour de l'examen</li>
          </ul>
        </div>

        <div>
          {done ? (
            <div className="rounded-xl border border-emerald-700/40 bg-emerald-950/15 p-6 text-center" data-testid="checklist-success">
              <CheckCircle2 size={32} className="text-emerald-400 mx-auto mb-3" />
              <div className="text-emerald-300 font-serif-editorial text-[20px]">Téléchargement lancé !</div>
              <p className="text-white/70 text-[13px] mt-2">Si le PDF n'apparaît pas, vérifie tes téléchargements ou autorise les pop-ups du site.</p>
              <button onClick={() => setDone(false)} className="mt-4 text-[12.5px] text-red-400 hover:text-red-300 font-semibold">Demander à nouveau →</button>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-3">
              <div>
                <label className="text-[10.5px] uppercase tracking-[0.18em] text-white/45 font-bold font-mono-spec block mb-1.5">Ton prénom (optionnel)</label>
                <input
                  data-testid="checklist-name"
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Camille"
                  className="w-full h-11 px-4 rounded-lg bg-white/5 border border-white/15 text-white text-[14px] outline-none focus:border-red-500"
                />
              </div>
              <div>
                <label className="text-[10.5px] uppercase tracking-[0.18em] text-white/45 font-bold font-mono-spec block mb-1.5">Ton email <span className="text-red-400">*</span></label>
                <div className="relative">
                  <Mail size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
                  <input
                    data-testid="checklist-email"
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="camille@exemple.fr"
                    className="w-full h-11 pl-9 pr-4 rounded-lg bg-white/5 border border-white/15 text-white text-[14px] outline-none focus:border-red-500"
                  />
                </div>
              </div>
              {err && <div className="text-red-400 text-[12.5px] bg-red-950/30 border border-red-900/40 rounded-lg px-3 py-2" data-testid="checklist-error">{err}</div>}
              <button
                type="submit"
                disabled={loading}
                data-testid="checklist-submit"
                className="w-full h-12 rounded-full bg-red-600 hover:bg-red-500 text-white text-[14px] font-semibold inline-flex items-center justify-center gap-2 shadow-lg shadow-red-900/40 disabled:opacity-50 transition"
              >
                {loading ? <><Loader2 size={15} className="animate-spin" /> Génération du PDF…</> : <><Download size={14} /> Recevoir la check-list PDF</>}
              </button>
              <p className="text-white/40 text-[11px] leading-relaxed">
                On t'envoie ponctuellement des ressources pédagogiques et des actus sécurité routière. Désinscription en 1 clic. Aucun spam.
              </p>
            </form>
          )}
        </div>
      </div>
    </section>
  );
}

export default function FormationParcoursPage() {
  return (
    <PageShell>
      <section className="max-w-6xl mx-auto px-4 lg:px-6 mt-10" data-testid="formation-parcours-page">
        {/* Breadcrumb */}
        <nav className="flex items-center gap-2 text-[12px] text-white/50 font-mono-spec mb-6">
          <Link to="/" className="hover:text-white transition">codevoiture.fr</Link>
          <span className="text-white/25">/</span>
          <Link to="/eleve" className="hover:text-white transition">Espace élève</Link>
          <span className="text-white/25">/</span>
          <span className="text-white/70">Formations & pré-requis</span>
        </nav>

        {/* Hero */}
        <div className="rounded-2xl sm:rounded-3xl border border-white/10 bg-gradient-to-br from-[#15090a] via-[#0d0d0e] to-[#0d0d0e] p-5 sm:p-7 lg:p-10 mb-12 relative overflow-hidden">
          <div className="pointer-events-none absolute -top-24 -right-24 w-80 h-80 rounded-full bg-red-600/10 blur-3xl" />
          <div className="relative z-10">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-red-700/40 bg-red-600/10 mb-4">
              <GraduationCap size={13} className="text-red-400" />
              <span className="text-[10.5px] uppercase tracking-[0.22em] text-red-400 font-bold">Espace élève · Quel parcours choisir ?</span>
            </div>
            <h1 className="font-serif-editorial text-white text-[36px] sm:text-[44px] lg:text-[56px] leading-[0.98] tracking-tight">
              4 parcours,<br />
              <span className="text-red-500">un seul permis B.</span>
            </h1>
            <p className="text-white/70 text-[15px] lg:text-[16px] mt-5 max-w-2xl leading-relaxed">
              Classique, AAC, Conduite Supervisée ou extension B96 : chaque chemin a ses pré-requis, ses RDV obligatoires et ses bénéfices. Voici tout ce que tu dois savoir avant de t'inscrire en auto-école.
            </p>
          </div>
        </div>

        {/* Pré-requis communs */}
        <SectionTitle
          kicker="Avant de démarrer"
          title="Les 6 pré-requis communs"
          sub="Quel que soit ton parcours, ces conditions sont obligatoires pour t'inscrire et passer le Code."
        />
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-16">
          {PRE_REQUIS_COMMUNS.map((p, i) => {
            const Icon = ICON_MAP[p.icon] || FileText;
            return (
              <div key={i} className="rounded-xl border border-white/10 bg-[#0d0d0e] p-5 hover:border-red-700/40 transition" data-testid={`pre-requis-${i}`}>
                <div className="w-10 h-10 rounded-lg bg-red-600/15 border border-red-700/30 flex items-center justify-center text-red-400 mb-3">
                  <Icon size={17} />
                </div>
                <h3 className="text-white font-serif-editorial text-[18px] leading-tight">{p.titre}</h3>
                <p className="text-white/60 text-[13px] mt-2 leading-relaxed">{p.desc}</p>
              </div>
            );
          })}
        </div>

        {/* Étapes communes */}
        <SectionTitle
          kicker="De l'inscription au permis probatoire"
          title="Les 6 étapes du parcours"
          sub="Toutes filières confondues, l'apprentissage suit le même ordre. Les seules différences : la durée totale et la présence éventuelle d'une phase accompagnée."
        />
        <ol className="relative mb-16">
          <div className="absolute left-[19px] top-2 bottom-2 w-px bg-red-700/30" />
          {ETAPES_COMMUNES.map((s) => (
            <li key={s.n} className="relative pl-12 pb-6" data-testid={`etape-${s.n}`}>
              <div className="absolute left-0 top-0 w-10 h-10 rounded-full bg-red-600 text-white font-mono-spec text-[13px] font-bold flex items-center justify-center shadow-lg shadow-red-900/40">
                {s.n}
              </div>
              <div className="rounded-xl border border-white/10 bg-[#0d0d0e] p-5">
                <div className="text-[10.5px] uppercase tracking-[0.22em] text-red-400 font-bold font-mono-spec">{s.label}</div>
                <h3 className="text-white font-serif-editorial text-[20px] leading-tight mt-1">{s.titre}</h3>
                <p className="text-white/65 text-[13.5px] mt-2 leading-relaxed">{s.desc}</p>
                <div className="mt-3 inline-flex items-center gap-1.5 text-[11.5px] font-mono-spec text-white/55">
                  <Clock size={11} /> {s.duree}
                </div>
              </div>
            </li>
          ))}
        </ol>

        {/* Parcours */}
        <SectionTitle
          kicker="Choisis ton parcours"
          title="4 filières · 4 rythmes"
          sub="Clique sur un parcours pour découvrir ses spécificités, ses avantages et — pour l'AAC et la Conduite Supervisée — les RDV obligatoires avec les parents accompagnateurs."
        />
        <div className="space-y-3 mb-16">
          {PARCOURS.map((p) => <ParcoursCard key={p.slug} p={p} />)}
        </div>

        {/* Lead capture · Check-list PDF */}
        <ChecklistLeadForm />

        {/* CTA */}
        <div className="rounded-xl sm:rounded-2xl border border-white/10 bg-gradient-to-br from-[#0d0d0e] to-[#15090a] p-5 sm:p-7 lg:p-9 text-center mb-10">
          <h3 className="font-serif-editorial text-white text-[26px] lg:text-[32px] leading-tight">Prêt à passer à la pratique ?</h3>
          <p className="text-white/65 text-[14px] mt-3 max-w-2xl mx-auto leading-relaxed">
            Une fois inscrit, prépare-toi compétence par compétence avec les <span className="text-red-400">38 leçons REMC chartrées</span> conçues par des moniteurs ECSR.
          </p>
          <Link to="/cours/eleve/voiture" data-testid="cta-cours-voiture" className="mt-5 inline-flex items-center gap-2 h-12 px-6 rounded-full bg-red-600 hover:bg-red-500 text-white text-[14px] font-semibold shadow-lg shadow-red-900/40 transition">
            <MapPin size={14} /> Accéder aux cours en voiture
          </Link>
        </div>

        {/* Footer back */}
        <div className="mb-10 pt-6 border-t border-white/5 flex items-center justify-between flex-wrap gap-3">
          <div className="text-[11.5px] text-white/40 font-mono-spec">Aligné REMC · arrêtés du 22/12/2009 modifiés · codevoiture.fr</div>
          <Link to="/eleve" data-testid="back-eleve-btn" className="inline-flex items-center gap-1.5 text-[13px] text-red-400 hover:text-red-300 font-semibold">
            <ArrowLeft size={13} /> Retour espace élève
          </Link>
        </div>
      </section>
    </PageShell>
  );
}
